import { cn } from '../../lib/cn'

interface GradientBackgroundProps {
	className?: string
}

/**
 * Gradient background with animated pulse effects.
 *
 * Ported verbatim from /loop reference (app/src/components/ui/gradient-background.tsx)
 * to match production motion feel: calm breath, not aurora drift. Two cyan/green
 * blobs pulse opacity 1 ↔ 0.5 over 2s via Tailwind's built-in `animate-pulse`.
 * 1s delay between blobs creates offset rhythm.
 *
 * Z-index note: uses `z-0` (not `-z-10`) because body/html have opaque
 * background `#050507` which would bury negative-z layers. Page content
 * paints on top via document order.
 */
export function GradientBackground({ className }: GradientBackgroundProps) {
	return (
		<div
			className={cn('fixed inset-0 z-0 pointer-events-none', className)}
			aria-hidden="true"
		>
			{/* Base gradient */}
			<div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-green-500/10" />
			{/* Animated pulse - top left */}
			<div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
			{/* Animated pulse - bottom right */}
			<div
				className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse"
				style={{ animationDelay: '1s' }}
			/>
		</div>
	)
}

/**
 * CSS to hide scrollbars across browsers.
 */
export function HideScrollbar() {
	return (
		<style>
			{`
      ::-webkit-scrollbar {display:none;}
      -ms-overflow-style: none;
      scrollbar-width: none;
      `}
		</style>
	)
}
